import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import {
  CreateFolderRequest,
  FOLDER_RESOURCE_TYPE_CANVAS,
  Folder,
  FolderResourceType,
  FolderResponse,
  ListFoldersResponse,
  UpdateFolderRequest,
} from "./types";

export * from "./types";

const ENDPOINTS = {
  LIST: "/api/v2/org/:organization_id/user/:user_id/folders",
  CREATE: "/api/v2/org/:organization_id/user/:user_id/folders",
  UPDATE: "/api/v2/org/:organization_id/user/:user_id/folders/:folder_id",
  DELETE: "/api/v2/org/:organization_id/user/:user_id/folders/:folder_id",
};

/** Shared, single-type folders that organize resources within a workspace. */
export class FoldersAPI {
  constructor(private client: CohesiveAPIClient) {}

  /** List the folders of one resource type in a workspace (includes empty ones). */
  async list(
    params: { workspaceId: string; resourceType?: FolderResourceType },
    config?: AxiosRequestConfig,
  ): Promise<Folder[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.get<ListFoldersResponse>(
      route(ENDPOINTS.LIST, {
        organization_id: organizationId,
        user_id: userId,
      }),
      {
        ...config,
        params: {
          ...config?.params,
          resource_type: params.resourceType ?? FOLDER_RESOURCE_TYPE_CANVAS,
          workspace_id: params.workspaceId,
        },
      },
    );
    return response.data.data.folders;
  }

  /** Create a folder. */
  async create(
    data: CreateFolderRequest,
    config?: AxiosRequestConfig,
  ): Promise<Folder> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.post<FolderResponse>(
      route(ENDPOINTS.CREATE, {
        organization_id: organizationId,
        user_id: userId,
      }),
      data,
      config,
    );
    return response.data.data;
  }

  /** Rename and/or move a folder. `parent_folder_id: null` files it at the top level. */
  async update(
    folderId: string,
    data: UpdateFolderRequest,
    config?: AxiosRequestConfig,
  ): Promise<Folder> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.patch<FolderResponse>(
      route(ENDPOINTS.UPDATE, {
        organization_id: organizationId,
        user_id: userId,
        folder_id: folderId,
      }),
      data,
      config,
    );
    return response.data.data;
  }

  /** Delete a folder; its subfolders and resources are re-parented to its parent. */
  async remove(folderId: string, config?: AxiosRequestConfig): Promise<void> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    await this.client.axios.delete(
      route(ENDPOINTS.DELETE, {
        organization_id: organizationId,
        user_id: userId,
        folder_id: folderId,
      }),
      config,
    );
  }
}
